import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { backendApi } from '@common-reducers/backend-api/backend-api';
import { addScheduledMessageButtonActions } from '@common-reducers/AddScheduledMessageButtonReducer';
import { selectCurrentUserPhone } from '@common-reducers/UserSelectors';
import { applySeedToDraft } from '@common/thunks/apply-seed-to-draft';
import { handleClickScheduleMessageThunk } from '@common/thunks/UserMessagesThunks';
import { closePanel } from '@common-reducers/BlueticksPanelReducer';
import { ExtendedUserMessage } from '@common-models/extend-user-message';
import { MessageState } from '@common/types/interface';
import { seedForStep } from './first-week-card-logic';

const emptyDraft: ExtendedUserMessage = {
  dueDate: undefined,
  message: '',
  isRecurring: false,
  state: MessageState.pending,
  cancelIfReceived: false,
} as ExtendedUserMessage;

/**
 * Opens the message composer pre-seeded for a first-week step — "send yourself a
 * test message" (to the user's own number, now) or "schedule a follow-up"
 * (tomorrow 10:00). One implementation for every surface that offers the step:
 * the onboarding row and the /connections guide.
 */
export function useOpenFirstWeekComposer(surface: 'web' | 'extension') {
  const dispatch = useAppDispatch();
  // Read the checklist the caller already fetched (row / page) straight from the RTK
  // cache — no subscription of its own, so it never triggers or keeps a request alive.
  const data = useAppSelector((s) => backendApi.endpoints.getFirstWeekChecklist.select(undefined)(s)?.data);
  // The checklist's ownWid can lag (cached before the number was known); the user's own phone —
  // written from the connected WhatsApp — is the same number, so the test always has a recipient.
  const userPhone = useAppSelector(selectCurrentUserPhone);
  const phoneDigits = String(userPhone ?? '').replace(/\D/g, '');
  const ownWid = data?.ownWid ?? (phoneDigits ? `${phoneDigits}@c.us` : null);

  return useCallback((step: 'self_test' | 'workflow') => {
    const seed = seedForStep(step, ownWid, new Date()) ?? undefined;
    if (surface === 'extension') {
      dispatch(handleClickScheduleMessageThunk({ seed }));
      dispatch(closePanel());
      return;
    }
    dispatch(addScheduledMessageButtonActions.editMessage(applySeedToDraft(emptyDraft, seed, null) as ExtendedUserMessage));
  }, [dispatch, ownWid, surface]);
}
